import type { CacheSettings } from "@growthbook/growthbook";

import { GrowthBook } from "@growthbook/growthbook";
import { computed, reactive, toRefs, watch } from "vue";

import type { ToMaybeRefs } from "@solvari/utils";

import {
  addDataToSentryProject,
  awaitTimeout,
  getEnv,
  isBot,
  isError,
  isSupportedBrowser,
  returnError,
} from "@solvari/utils";

import type { PageAttributes } from "@/service/attributes";

import { getSentryAbTesting } from "@/client";
import { addAbTestEvent } from "@/service/analytics";
import { getBrowserAttributes } from "@/service/attributes";
import { splitUrlFeature, splitUrlMapFeature } from "@/service/features";

type SiteAttributes = {
  expertSiteTheme?: number | string | null;
  hasJumpLinks?: boolean;
  // TODO: LocaleMorpheus duplicate to prevent circular dependencies
  siteLocale: "da-DK" | "fr-BE" | "fr-FR" | "nl-BE" | "nl-NL" | "sv-SE";
  siteType: "expert" | "platform" | "satellite";
};

async function initGrowthBook(
  solvariClientId: string,
  siteAttributes: ToMaybeRefs<SiteAttributes>,
) {
  const pageAttributes: PageAttributes = reactive({
    formGroupId: null,
    formId: null,
  });

  const attributes = reactive({
    solvariClientId,
    ...siteAttributes,
    ...getBrowserAttributes(),
    ...toRefs(pageAttributes),
    hasForm: computed(
      () => !!pageAttributes.formId || !!pageAttributes.formGroupId,
    ),
  });

  const growthBook = reactive(
    new GrowthBook({
      apiHost: "https://cdn.growthbook.io",
      clientKey: getEnv().integrations.growthbook.clientKey,
      attributes,
      trackingCallback: addAbTestEvent, // When a feature gets triggered it will send an event to gtm and Argus
      enableDevMode: true, // This adds support for the browser extension, this only affects the front end so it's fine to run in prod
    }),
  );

  watch(attributes, (attributes) => {
    void growthBook.setAttributes(attributes);
  });

  addDataToSentryProject("mkt-ab-testing", () => ({
    contexts: {
      attributes: growthBook.getAttributes(),
      features: growthBook.getFeatures() as Record<string, unknown>,
    },
  }));

  const result = await returnError(() =>
    growthBook.init({
      streaming: false,
      cacheSettings: { disableCache: true } as CacheSettings,
    }),
  )();

  if (isError(result)) {
    getSentryAbTesting().captureException(result);
  } else if (result.error) {
    getSentryAbTesting().captureException(result.error);
  }

  // Bind it to the window so that other apps can check for features without a second instance
  globalThis.abTesting = growthBook;
  globalThis.abTestingAttributes = pageAttributes;

  try {
    splitUrlFeature();
    splitUrlMapFeature();
  } catch (error) {
    getSentryAbTesting().captureException(error);
  }
}

/**
 * Initializes GrowthBook A/B testing with site attributes.
 * Skips initialization for bots and unsupported browsers. Triggers anti-flicker render.
 *
 * @param solvariClientId - The Solvari client ID from the Argus session
 * @param siteAttributes - Site-specific attributes for targeting
 * @param siteAttributes.siteLocale - The locale of the site (e.g., "nl-NL", "fr-BE")
 * @param siteAttributes.siteType - The type of site ("platform", "expert", or "satellite")
 * @param siteAttributes.expertSiteTheme - Optional theme ID for expert sites
 * @param siteAttributes.hasJumpLinks - Optional flag indicating if the page has jump links
 * @example
 * const argusSession = initArgus();
 * await initAbTesting(argusSession.clientId, {
 *   siteLocale: localeToIso(getEnv().config.localeCode),
 *   siteType: "platform",
 * });
 */
async function initAbTesting(
  solvariClientId: string,
  siteAttributes: SiteAttributes,
) {
  if (!isBot() && isSupportedBrowser()) {
    await Promise.race([
      initGrowthBook(solvariClientId, siteAttributes),
      awaitTimeout(1000),
    ]);
  }

  // Renders the page in case it was hidden by an anti flicker snippet
  if (globalThis.abTestingRender) {
    globalThis.abTestingRender();
  }
}

export { initAbTesting };
